import React, { createContext, useReducer, useMemo } from 'react';
import { getApiUrl } from '../config';
import axios from 'axios';

export const calculateMonthlyPayment = (totalAmount, installmentCount, installmentRate) => {
  if (installmentCount > 0) {
    const total = Number(totalAmount);
    const rate = Number(installmentRate);
    const count = Number(installmentCount);
    if (isNaN(total) || isNaN(rate) || isNaN(count)) return 0;

    return ((total * (1 + rate / 100)) / count).toFixed(2);
  }
  return 0;
};

const initialState = {
  cardHolderName: '',
  cardNumber: '',
  expiryDate: '',
  cvc: '',
  totalAmount: '',
  installments: [],
  commissionRate: [],
  selectedInstallment: '',
  paymentSuccess: false,
  loading: false,
  error: null,
  userBalance: {
    site_kredisi: 0,
    cekilebilir_bakiye: 0,
    toplam_cekilen: 0
  },
  paymentDistribution: null,
};

const paymentReducer = (state, action) => {
  switch (action.type) {
    case 'SET_CARD_NUMBER':
      return { ...state, cardNumber: action.payload };
    case 'SET_CARD_HOLDER_NAME':
      return { ...state, cardHolderName: action.payload };
    case 'SET_EXPIRY_DATE':
      return { ...state, expiryDate: action.payload };
    case 'SET_CVC':
      return { ...state, cvc: action.payload };
    case 'FETCH_INSTALLMENTS_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_INSTALLMENTS_SUCCESS':
      return { ...state, loading: false, installments: action.payload };
    case 'FETCH_INSTALLMENTS_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'FETCH_COMMISSION_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_COMMISSION_SUCCESS':
      return { ...state, loading: false, commissionRate: action.payload };
    case 'FETCH_COMMISSION_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_SELECTED_INSTALLMENT':
      return { ...state, selectedInstallment: action.payload };
    case 'SET_TOTAL_AMOUNT':
      return { ...state, totalAmount: action.payload };
    case 'PAYMENT_REQUEST':
      return { ...state, loading: true, paymentSuccess: false, error: null };
    case 'PAYMENT_SUCCESS':
      return { ...state, loading: false, paymentSuccess: true };
    case 'PAYMENT_ERROR':
      return { ...state, loading: false, error: action.payload };
    case 'SET_USER_BALANCE':
      return { ...state, userBalance: action.payload };
    case 'SET_PAYMENT_DISTRIBUTION':
      return { ...state, paymentDistribution: action.payload };
    default:
      return state;
  }
};

export const PaymentContext = createContext();

export const PaymentProvider = ({ children }) => {
  const [state, dispatch] = useReducer(paymentReducer, initialState);

  const setCardNumber = (number) => {
    dispatch({ type: 'SET_CARD_NUMBER', payload: number });
  };
  const setCardHolderName = (name) => {
    dispatch({ type: 'SET_CARD_HOLDER_NAME', payload: name });
  };

  const setExpiryDate = (month) => {
    dispatch({ type: 'SET_EXPIRY_DATE', payload: month });
  };

  const setCvc = (cvc) => {
    dispatch({ type: 'SET_CVC', payload: cvc });
  };

  const setTotalAmount = (total) => {
    dispatch({ type: 'SET_TOTAL_AMOUNT', payload: total });
  };

  const fetchUserBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/user/balance`, {
        method: 'GET',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (result) {
        dispatch({ type: 'SET_USER_BALANCE', payload: result });
        return result;
      }
    } catch (error) {
      console.error('Bakiye bilgisi alınırken hata:', error);
    }
    return null;
  };

  const calculatePaymentDistribution = async (totalAmount) => {
    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/calculate_payment`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ total_amount: totalAmount }),
      });

      const result = await response.json();

      if (result.success && result.payment_distribution) {
        dispatch({ type: 'SET_PAYMENT_DISTRIBUTION', payload: result.payment_distribution });
        return result.payment_distribution;
      }
    } catch (error) {
      console.error('Ödeme dağılımı hesaplanırken hata:', error);
    }
    return null;
  };

  const fetchCommissionRates = async () => {
    dispatch({ type: 'FETCH_COMMISSION_REQUEST' });
    try {
      const tkn = localStorage.getItem('tkn');
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiUrl()}/bank_list`, {
        method: 'GET',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',

        },

      });
      const result = await response.json();

      if (result.data && typeof result.data === 'object') {
        const installmentData = result.data;

        const bankGroups = {};

        Object.entries(installmentData).forEach(([installmentCount, installmentArray]) => {
          if (Array.isArray(installmentArray)) {
            installmentArray.forEach(item => {
              const bankKey = item.card_program;

              if (!bankGroups[bankKey]) {
                bankGroups[bankKey] = {
                  name: item.card_program,
                  image: '',
                  taksit: []
                };
              }

              const existingInstallment = bankGroups[bankKey].taksit.find(t => t.count === parseInt(installmentCount));
              if (!existingInstallment) {
                bankGroups[bankKey].taksit.push({
                  count: parseInt(installmentCount),
                  rate: item.user_commission_percentage
                });
              }
            });
          }
        });

        const formattedData = Object.values(bankGroups);
        dispatch({ type: 'FETCH_COMMISSION_SUCCESS', payload: formattedData });
      } else {
        throw new Error('Geçersiz veri formatı');
      }
    } catch (error) {
      console.error('API çağrısı sırasında hata oluştu:', error);
      dispatch({ type: 'FETCH_COMMISSION_ERROR', payload: error.message });
    }
  };

  const fetchInstallments = async (card_number) => {
    const cardDetails = {
      card_number: card_number,
    };

    try {
      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`${getApiUrl()}/card_info`, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(cardDetails),
      });

      const data = await response.json();
      // fbq varsa kullan yoksa hata vermesin diye kontrol eklenebilir
      if (typeof fbq !== 'undefined') fbq('track', 'AddPaymentInfo');

      const formattedInstallments = Object.entries(data.installment_rates).map(
        ([count, rate]) => ({
          count: parseInt(count),
          rate: rate,
          totalAmount: calculateMonthlyPayment(state.totalAmount, parseInt(count), rate),
        })
      );

      dispatch({ type: 'FETCH_INSTALLMENTS_SUCCESS', payload: formattedInstallments });
    } catch (error) {
      dispatch({ type: 'FETCH_INSTALLMENTS_ERROR', payload: error.message });
    }
  };

  const setSelectedInstallment = (installment) => {
    dispatch({ type: 'SET_SELECTED_INSTALLMENT', payload: installment });
  };

  const paymentResult = async (allParams) => {
    try {
      if (!allParams.ipAddress) {
        const userIp = await getUserIp();
        allParams = { ...allParams, ipAddress: userIp };
      }

      const response = await fetch(`${getApiUrl()}/payment/success`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(allParams),
      });

      if (!response.ok) {
        throw new Error('Veri gönderme işlemi başarısız oldu.');
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error('Veri gönderilirken bir hata oluştu:', error);
    }
  };

  const getUserIp = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.error('IP adresi alınamadı:', error);
      return null;
    }
  };

  const processPayment = async (paymentData, showToastFn) => {
    dispatch({ type: 'PAYMENT_REQUEST' });
    const token = localStorage.getItem('token');
    const tkn = localStorage.getItem('tkn');

    try {
      if (!paymentData) {
        throw new Error('Ödeme verileri bulunamadı');
      }

      if (paymentData.musteriForm) {
        paymentData.musteriForm = paymentData.musteriForm || {};
      }

      const userIp = await getUserIp();
      const paymentDataWithIp = {
        ...paymentData,
        ipAddress: userIp
      };

      const response = await axios.post(`${getApiUrl()}/send_order2`, paymentDataWithIp, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response || !response.data) {
        throw new Error('API yanıtı geçersiz format');
      }

      if (response.data.success) {
        dispatch({ type: 'PAYMENT_SUCCESS' });
        if (typeof fbq !== 'undefined') fbq('track', 'InitiateCheckout');
        return {
          success: true,
          redirect_url: response.data.redirect_url,
          orderId: response.data.order_id,
        };
      } else {
        let errorMessage = 'Ödeme işlemi sırasında hata oluştu';

        if (response.data.error_code) {
          errorMessage = response.data.error || 'Ödeme Hatası';
        } else if (response.status === 404) {
          errorMessage = 'Kart bilgileri doğrulanamadı. Lütfen bilgilerinizi kontrol ediniz.';
        } else if (!paymentData.taksit_sayisi) {
          errorMessage = 'Lütfen taksit seçeneğini belirtiniz.';
        }

        dispatch({ type: 'PAYMENT_ERROR', payload: errorMessage });
        if (showToastFn) showToastFn(errorMessage, 'error');
        return { success: false, message: errorMessage };
      }
    } catch (error) {
      console.error("Payment Error:", error);
      let errorMessage = 'Ödeme işlemi sırasında bir hata oluştu';

      if (error.response) {
        // Error response logic...
        errorMessage = error.response.data.error || 'Beklenmeyen hata';
      }

      dispatch({ type: 'PAYMENT_ERROR', payload: errorMessage });
      if (showToastFn) showToastFn(errorMessage, 'error');
      return { success: false, message: errorMessage };
    }
  };

  const confirmEftPayment = async (orderNumber, orderAmount, showToastFn) => {
    dispatch({ type: 'PAYMENT_REQUEST' });
    try {
      if (!orderNumber) {
        throw new Error('Sipariş numarası (Order ID) gereklidir');
      }

      const orderNumberStr = String(orderNumber);
      const orderId = orderNumberStr.startsWith('EFT-') ? orderNumberStr.substring(4) : orderNumberStr;

      const userIp = await getUserIp();

      const data = {
        order: orderId,
        tutar: orderAmount,
        ipAddress: userIp
      };

      const token = localStorage.getItem('token');
      const tkn = localStorage.getItem('tkn');

      const response = await axios.post(`${getApiUrl()}/payment/success_eft`, data, {
        method: 'POST',
        headers: {
          'Authorization': `${token}`,
          'Tkn': `${tkn}`,
          'Content-Type': 'application/json',
        }
      });

      if (response.data.success) {
        dispatch({ type: 'PAYMENT_SUCCESS' });

        if (typeof fbq !== 'undefined') {
          fbq('track', 'Purchase', {
            currency: 'TRY',
            value: response.data.price
          });
        }

        if (showToastFn) showToastFn('EFT ödemeniz başarıyla bildirildi.', 'success');

        return {
          success: true,
          price: response.data.price,
          message: 'EFT ödemeniz başarıyla bildirildi.'
        };
      } else {
        const errorMessage = response.data.message || 'EFT bildirimi yapılırken bir hata oluştu';
        dispatch({ type: 'PAYMENT_ERROR', payload: errorMessage });
        if (showToastFn) showToastFn(errorMessage, 'error');

        return { success: false, message: errorMessage };
      }
    } catch (error) {
      console.error("EFT Payment Error:", error);
      const errorMessage = error.message || 'EFT bildirimi yapılırken bir hata oluştu';
      dispatch({ type: 'PAYMENT_ERROR', payload: errorMessage });
      if (showToastFn) showToastFn(errorMessage, 'error');

      return { success: false, message: errorMessage };
    }
  };

  const contextValue = useMemo(() => ({
    state,
    setCardNumber,
    setCardHolderName,
    setExpiryDate,
    setCvc,
    setTotalAmount,
    fetchCommissionRates,
    fetchInstallments,
    setSelectedInstallment,
    processPayment,
    paymentResult,
    calculateMonthlyPayment,
    confirmEftPayment,
    fetchUserBalance,
    calculatePaymentDistribution
  }), [state]);

  return (
    <PaymentContext.Provider value={contextValue}>
      {children}
    </PaymentContext.Provider>
  );
};